/** The learner's own numbers: mastery per level, quiz accuracy, streaks. */

import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { ApiError, LEVELS, api, levelColor, type Level, type Scorecard as ScorecardData } from '../api'
import type { QuizReview as QuizReviewData } from '../api'
import QuizReview from '../components/QuizReview'
import { EmptyState, ErrorNote, LevelBadge, Progressbar, Spinner, Stat } from '../components/ui'

const MODE_LABELS: Record<string, string> = {
  meaning: 'Meaning',
  reading: 'Reading',
  component: 'Components',
  kanji_from_meaning: 'Meaning → kanji',
  mixed: 'Mixed',
}

function errText(err: unknown): string {
  return err instanceof ApiError ? err.message : 'Could not reach the server.'
}

function pct(fraction: number): string {
  return `${Math.round(fraction * 100)}%`
}

/** Quiz history rows arrive as loose records, so every field is read defensively. */
function field(row: Record<string, string | number>, key: string): string {
  const value: string | number | undefined = row[key]
  return value === undefined ? '' : String(value)
}

function asLevel(value: string): Level | null {
  return (LEVELS as string[]).includes(value) ? (value as Level) : null
}

function readableDate(iso: string): string {
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return iso || '—'
  return date.toLocaleString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

export default function Scorecard() {
  const [data, setData] = useState<ScorecardData | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const [openId, setOpenId] = useState<number | null>(null)
  const [review, setReview] = useState<QuizReviewData | null>(null)
  const [reviewLoading, setReviewLoading] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  // Guards against a slow first request overwriting a newer selection.
  const wantedQuiz = useRef<number | null>(null)

  const openQuiz = useCallback(
    async (id: number) => {
      if (openId === id) {
        setOpenId(null)
        setReview(null)
        setReviewError(null)
        return
      }
      wantedQuiz.current = id
      setOpenId(id)
      setReview(null)
      setReviewError(null)
      setReviewLoading(true)
      try {
        const detail = await api.quizReview(id)
        if (wantedQuiz.current === id) setReview(detail)
      } catch (err) {
        if (wantedQuiz.current === id) setReviewError(errText(err))
      } finally {
        if (wantedQuiz.current === id) setReviewLoading(false)
      }
    },
    [openId],
  )

  useEffect(() => {
    let alive = true
    void (async () => {
      setLoading(true)
      setError(null)
      try {
        const card = await api.scorecard()
        if (alive) setData(card)
      } catch (err) {
        if (alive) setError(errText(err))
      } finally {
        if (alive) setLoading(false)
      }
    })()
    return () => {
      alive = false
    }
  }, [])

  if (loading) {
    return (
      <div className="mx-auto w-full max-w-5xl px-5 py-8">
        <Spinner label="Adding up your progress…" />
      </div>
    )
  }

  if (error || !data) {
    return (
      <div className="mx-auto w-full max-w-5xl px-5 py-8">
        <ErrorNote>{error ?? 'Scorecard unavailable.'}</ErrorNote>
      </div>
    )
  }

  const corpus = data.levels.reduce((sum, l) => sum + l.total, 0)
  const modes = Object.entries(data.accuracy_by_mode)
  const noActivity = data.total_seen === 0 && data.quizzes_taken === 0 && data.flashcards === 0

  return (
    <div className="mx-auto w-full max-w-5xl space-y-6 px-5 py-8">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-100">Scorecard</h1>
          <p className="text-sm text-ink-500">{data.user.display_name}</p>
        </div>
        <div className="flex items-center gap-2 text-xs text-ink-500">
          <span>Target</span>
          <LevelBadge level={data.user.target_level} />
        </div>
      </header>

      {noActivity ? (
        <EmptyState
          title="Nothing to score yet"
          body="Study a handful of kanji or take one quiz and this page fills up with mastery, accuracy and streaks."
          action={
            <div className="flex flex-wrap justify-center gap-2">
              <Link className="btn-primary" to={`/learn?level=${data.user.target_level}`}>
                Start studying
              </Link>
              <Link className="btn-ghost" to={`/quiz?level=${data.user.target_level}`}>
                Take a quiz
              </Link>
            </div>
          }
        />
      ) : (
        <>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 xl:grid-cols-5">
            <Stat label="Mastered" value={data.total_mastered} hint={`of ${corpus} kanji`} />
            <Stat label="Mastery" value={`${data.mastery_pct}%`} hint={`${data.total_seen} started`} />
            <Stat label="Quizzes" value={data.quizzes_taken} hint={`${data.questions_answered} questions`} />
            <Stat label="Accuracy" value={pct(data.overall_accuracy)} hint="across all quizzes" />
            <Stat label="Best quiz" value={pct(data.best_accuracy)} hint="single best run" />
            <Stat label="Streak" value={data.current_streak_days} hint="days in a row" />
            <Stat label="Longest streak" value={data.longest_streak_days} hint="days" />
            <Stat label="Flashcards" value={data.flashcards} hint="saved for later" />
            <Stat label="Due today" value={data.due_today} hint="cards waiting" />
          </div>

          <section className="card overflow-hidden">
            <h2 className="px-5 py-3 text-sm font-semibold uppercase tracking-wide text-ink-500">By level</h2>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-y border-ink-700/60 text-left text-[10px] uppercase tracking-wide text-ink-500">
                  <th className="px-5 py-2 font-semibold">Level</th>
                  <th className="px-3 py-2 text-right font-semibold">Kanji</th>
                  <th className="px-3 py-2 text-right font-semibold">Seen</th>
                  <th className="px-3 py-2 text-right font-semibold">Mastered</th>
                  <th className="w-2/5 px-5 py-2 font-semibold">Progress</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-ink-700/40">
                {data.levels.map((row) => (
                  <tr key={row.level}>
                    <td className="px-5 py-2.5">
                      <LevelBadge level={row.level} />
                    </td>
                    <td className="px-3 py-2.5 text-right text-ink-500">{row.total}</td>
                    <td className="px-3 py-2.5 text-right text-slate-200">{row.seen}</td>
                    <td className="px-3 py-2.5 text-right font-semibold text-slate-100">{row.mastered}</td>
                    <td className="px-5 py-2.5">
                      <Progressbar value={row.mastered} max={row.total} color={levelColor[row.level]} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </section>

          <section className="card space-y-4 p-5">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-ink-500">Accuracy by question type</h2>
            {modes.length === 0 ? (
              <p className="text-sm text-ink-500">No quizzes finished yet.</p>
            ) : (
              <div className="space-y-3">
                {modes.map(([mode, fraction]) => (
                  <div key={mode} className="space-y-1.5">
                    <div className="flex justify-between text-xs">
                      <span className="text-slate-200">{MODE_LABELS[mode] ?? mode}</span>
                      <span className="font-semibold text-ink-500">{pct(fraction)}</span>
                    </div>
                    <Progressbar value={Math.round(fraction * 100)} max={100} />
                  </div>
                ))}
              </div>
            )}
          </section>

          <section className="card overflow-hidden">
            <h2 className="px-5 py-3 text-sm font-semibold uppercase tracking-wide text-ink-500">Recent quizzes</h2>
            {data.recent_quizzes.length === 0 ? (
              <p className="px-5 pb-5 text-sm text-ink-500">
                No finished quizzes yet.{' '}
                <Link className="font-semibold text-brand-500 hover:underline" to="/quiz">
                  Take one
                </Link>
                .
              </p>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-y border-ink-700/60 text-left text-[10px] uppercase tracking-wide text-ink-500">
                    <th className="px-5 py-2 font-semibold">Level</th>
                    <th className="px-3 py-2 font-semibold">Type</th>
                    <th className="px-3 py-2 text-right font-semibold">Score</th>
                    <th className="px-5 py-2 text-right font-semibold">Finished</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-ink-700/40">
                  {data.recent_quizzes.map((row, i) => {
                    const mode = field(row, 'mode')
                    const id = Number(field(row, 'id'))
                    const isOpen = openId === id
                    return (
                      <tr
                        key={id || i}
                        className={`cursor-pointer transition hover:bg-ink-850/70 ${
                          isOpen ? 'bg-ink-850' : ''
                        }`}
                        onClick={() => Number.isFinite(id) && id > 0 && void openQuiz(id)}
                      >
                        <td className="px-5 py-2.5">
                          <LevelBadge level={asLevel(field(row, 'level'))} />
                        </td>
                        <td className="px-3 py-2.5 text-ink-500">{MODE_LABELS[mode] ?? mode}</td>
                        <td className="px-3 py-2.5 text-right font-semibold text-slate-100">
                          {field(row, 'score')} / {field(row, 'total')}
                        </td>
                        <td className="px-5 py-2.5 text-right text-ink-500">
                          <span className="mr-2">{readableDate(field(row, 'finished_at'))}</span>
                          <button
                            type="button"
                            aria-expanded={isOpen}
                            onClick={(e) => {
                              e.stopPropagation()
                              void openQuiz(id)
                            }}
                            className="rounded-lg border border-ink-700 bg-ink-850 px-2 py-0.5 text-[10px]
                                       font-semibold text-slate-300 transition hover:border-brand-500/60 hover:text-white"
                          >
                            {isOpen ? 'Hide' : 'Review'}
                          </button>
                        </td>
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </section>

          {/* A re-opened past attempt, rendered with the same component the quiz
              result screen uses so the two views cannot drift apart. */}
          {openId !== null && (
            <section className="space-y-3">
              {reviewLoading && <Spinner label="Loading that attempt…" />}
              {reviewError && <ErrorNote>{reviewError}</ErrorNote>}
              {review && (
                <>
                  <div className="card flex flex-wrap items-center gap-x-5 gap-y-2 px-5 py-3">
                    <div className="flex items-center gap-2">
                      <LevelBadge level={review.level} />
                      <span className="text-sm font-semibold text-slate-100">
                        {MODE_LABELS[review.mode] ?? review.mode}
                      </span>
                    </div>
                    <span className="text-sm text-slate-300">
                      {review.score} / {review.total}{' '}
                      <span className="text-ink-500">({pct(review.accuracy)})</span>
                    </span>
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        review.passed ? 'bg-brand-500/20 text-brand-500' : 'bg-rose-500/20 text-rose-300'
                      }`}
                    >
                      {review.passed ? 'Passed' : 'Not passed'}
                    </span>
                    <span className="text-xs text-ink-500">
                      {readableDate(review.finished_at ?? review.started_at)}
                    </span>
                    <button
                      type="button"
                      onClick={() => {
                        setOpenId(null)
                        setReview(null)
                        setReviewError(null)
                      }}
                      className="ml-auto btn-ghost !px-2 !py-1 text-xs"
                    >
                      Close
                    </button>
                  </div>
                  <QuizReview items={review.items} level={review.level} />
                </>
              )}
            </section>
          )}
        </>
      )}
    </div>
  )
}
